/**
 * Whether an AI has ever connected to this vault, as the server reports it.
 *
 * An empty vault has two explanations that call for different next steps —
 * nothing has been written yet, or nothing has ever connected — and only the
 * server can say which. The fact rides on `GET /v1/platform/counts` beside the
 * badge counts, so the empty state and the account dialog learn it whenever
 * the counts are reconciled.
 *
 * Three states, kept distinct on purpose: the server said "never" (`null`),
 * the server named a time (an object), or the server did not say
 * (`undefined` — an older deployment, or an unreadable payload). The last one
 * must never be rendered as "no AI has connected", because it is not that.
 */

export interface McpActivity {
  /** When the newest call happened, ISO 8601, by the server's clock. */
  readonly at: string;
  /** What made it, in the log's words: `group.action`. */
  readonly via: string;
}

/** Reads the `lastMcpActivity` field of a counts payload, or `undefined` when it is absent or unreadable. */
export function parseMcpActivity(payload: unknown): McpActivity | null | undefined {
  if (typeof payload !== "object" || payload === null || !("lastMcpActivity" in payload)) return undefined;
  const value = (payload as { lastMcpActivity: unknown }).lastMcpActivity;
  if (value === null) return null;
  if (typeof value !== "object") return undefined;
  const { at, via } = value as { at?: unknown; via?: unknown };
  if (typeof at !== "string" || Number.isNaN(Date.parse(at))) return undefined;
  return { at, via: typeof via === "string" && via.length > 0 ? via : "an AI" };
}

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

/** "3 minutes ago", "just now", never a negative interval when clocks disagree. */
export function relativeTime(at: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - Date.parse(at);
  if (elapsed < 60 * 1000) return "just now";
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "always" });
  for (const [unit, size] of UNITS) {
    if (elapsed >= size) return formatter.format(-Math.floor(elapsed / size), unit);
  }
  return "just now";
}

/**
 * The one line both surfaces show. The web client authors nothing, so the copy
 * is a fact and a pointer — the action it invites is asking an AI, which the
 * buttons next to it already offer.
 */
export function mcpActivityLabel(activity: McpActivity | null | undefined, now: Date = new Date()): string {
  if (activity === undefined) return "";
  if (activity === null) return "No AI has connected yet. Once one has, its activity shows here.";
  return `Last AI activity: ${relativeTime(activity.at, now)} via ${activity.via}.`;
}
